import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./client";

export type AlertLevel = "expired" | "very_soon" | "soon";
export type AlertType = "insurance" | "inspection" | "service" | "maintenance";
export type AlertSeverity = "info" | "medium" | "high";

export type VehicleTrackingAlert = {
  vehicle_id: string;
  plate_no: string;
  brand_model: string | null;
  branch_id: string;
  current_km: number;
  alert_type: AlertType;
  alert_level: AlertLevel;
  days_left: number | null;
  km_left: number | null;
  deadline_date: string | null;
  deadline_km: number | null;
  dedupe_key: string;
  title: string;
  message: string;
  severity: AlertSeverity;
};

export type VehicleTrackingSummary = {
  generated_at: string;
  total: number;
  counts: {
    expired: number;
    very_soon: number;
    soon: number;
    by_type: {
      insurance: number;
      inspection: number;
      service: number;
      maintenance: number;
    };
  };
  alerts: VehicleTrackingAlert[];
};

export type SummaryState = {
  data: VehicleTrackingSummary | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
};

export function useVehicleTrackingSummary(branchId?: string): SummaryState {
  const [data, setData] = useState<VehicleTrackingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<VehicleTrackingSummary>(
        "/vehicle-tracking/summary",
        { branch_id: branchId || undefined },
        { signal: controller.signal },
      )
      .then((res) => setData(res))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Araç uyarıları yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [branchId, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  return { data, loading, error, reload };
}
